import { useState } from 'react';
import {
  AdminSpinner,
  ArrowRed,
  Box,
  Disclaimer,
  Div,
  FinancesGamesEntity,
  H2,
  H3,
  Icon,
  P,
  TableHeader,
  lightTheme,
} from '@vgrubs/components';
import {
  CollectionBar,
  CollectionLegend,
  CollectionPart,
  LegendItem,
  SmallDot,
} from '../ClientOutstandingBalance.styled';
import { GAMES_COLUMNS, GamesPlayerRow } from './GamesPlayerRow';
import { count, gamesRange, plural, shareOf, usd } from './gamesFormat';
import { GamesPlayersModal } from './GamesPlayersModal';
import { useGamesPlayers } from './useGamesPlayers';
import { useGamesSummary } from './useGamesSummary';

/** The card shows the top of the list; the arrow opens the rest. */
const CARD_ROWS = 5;

type Props = {
  from: string;
  to: string;
  /** The selected range in words, beside "Our share". */
  periodLabel?: string;
};

/**
 * "Play & Earn Revenue": what MyChips paid for the games on the vDrive
 * tracker in the range, how much of it went to the players as points and
 * what was left for us, then the players themselves. Admin only, and not part of the income
 * above: it is a view of its own.
 */
export const GamesFinances = ({ from, to, periodLabel }: Props) => {
  const range = gamesRange(from, to);
  const { summary, isLoading, isError } = useGamesSummary(range);
  const players = useGamesPlayers(range);
  const [listOpen, setListOpen] = useState(false);

  return (
    <Div $mb="32" $mbSM="20">
      <H3 $mb="16">Play &amp; Earn Revenue</H3>
      <Box $relative $flex $column $gap="24px" $p="20px 24px">
        {isLoading && (
          <AdminSpinner small absolute overflowBorderRadius="20px" creme />
        )}
        {players.players.length > 0 && (
          <Icon
            $pointer
            color={lightTheme.lightGray}
            aria-label="Open all players"
            style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
            onClick={() => setListOpen(true)}
          >
            <ArrowRed />
          </Icon>
        )}

        {/* A failed summary would otherwise read as $0.00, as if no one
            had played. */}
        {isError ? (
          <P $m="0" $center $color={lightTheme.lightGray}>
            The games could not be loaded.
          </P>
        ) : (
          <>
            <Div>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                Our share{periodLabel ? `, ${periodLabel}` : ''}
              </Disclaimer>
              <H2
                $m="0"
                $color={
                  (summary?.ourShare ?? 0) < 0
                    ? lightTheme.primary
                    : lightTheme.green
                }
              >
                {usd(summary?.ourShare ?? 0)}
              </H2>
              {summary && <SummaryLine summary={summary} />}
              {summary && <ShareBar summary={summary} />}
            </Div>

            {players.players.length === 0 ? (
              !players.isLoading && (
                <P $m="0" $center $color={lightTheme.lightGray}>
                  No one played in this range.
                </P>
              )
            ) : (
              <Div>
                <TableHeader
                  columns={GAMES_COLUMNS}
                  padding="0 16px"
                  gap="12px"
                />
                {players.players.slice(0, CARD_ROWS).map((player) => (
                  <GamesPlayerRow key={player.gamesUserId} player={player} />
                ))}
              </Div>
            )}
          </>
        )}
      </Box>

      <GamesPlayersModal
        isOpen={listOpen}
        onClose={() => setListOpen(false)}
        range={range}
        periodLabel={periodLabel}
      />
    </Div>
  );
};

/** "MyChips paid $1,248.60 · 214 players · 1,382 tasks · 9 taken back". */
const SummaryLine = ({ summary }: { summary: FinancesGamesEntity }) => (
  <Disclaimer $m="0" $color={lightTheme.lightGray}>
    {[
      `MyChips paid ${usd(summary.revenue)}`,
      plural(summary.players, 'player'),
      plural(summary.conversions, 'task'),
      summary.deductions > 0 && `${count(summary.deductions)} taken back`,
    ]
      .filter(Boolean)
      .join(' · ')}
  </Disclaimer>
);

/**
 * What MyChips paid as one bar, ours and the players'. No bar when it paid
 * nothing; a negative share of ours (a CPI offer) draws as none of the bar.
 * The points still held are in points, so they sit in the legend only.
 */
const ShareBar = ({ summary }: { summary: FinancesGamesEntity }) => {
  const ours = shareOf(summary.ourShare, summary.revenue);
  const theirs = shareOf(summary.playersShare, summary.revenue);
  const oursWidth = Math.min(100, Math.max(0, ours ?? 0));
  const parts = [
    {
      key: 'ours',
      color: summary.ourShare < 0 ? lightTheme.primary : lightTheme.green,
      text: `Our share ${usd(summary.ourShare)}${
        ours != null ? ` (${ours}%)` : ''
      }`,
    },
    {
      key: 'players',
      color: lightTheme.blue,
      text: `Players got ${usd(summary.playersShare)}${
        theirs != null ? ` (${theirs}%)` : ''
      }`,
    },
    {
      key: 'points',
      color: lightTheme.lightGray,
      text: `Points not redeemed ${count(summary.pointsBalance)}`,
    },
  ];

  return (
    <>
      {summary.revenue > 0 && (
        <CollectionBar
          role="img"
          aria-label={parts
            .slice(0, 2)
            .map((part) => part.text)
            .join(', ')}
        >
          <CollectionPart $color={lightTheme.green} $width={oursWidth} />
          <CollectionPart $color={lightTheme.blue} $width={100 - oursWidth} />
        </CollectionBar>
      )}
      <CollectionLegend>
        {parts.map((part) => (
          <LegendItem key={part.key}>
            <SmallDot $color={part.color} />
            <Disclaimer
              $m="0"
              $color={part.key === 'points' ? lightTheme.lightGray : part.color}
            >
              {part.text}
            </Disclaimer>
          </LegendItem>
        ))}
      </CollectionLegend>
    </>
  );
};
